import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SELF_CANCELLABLE_STATUSES, type LocalizedText } from '@cr/shared';
import { api, errorMessage } from '@/lib/api';
import { dateRange, localized, money } from '@/lib/format';
import { useSession } from '@/lib/session';
import { haptic, openLink, setBackButton } from '@/lib/telegram';
import Icon from '@/components/icon';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Row,
  Screen,
  SectionTitle,
  Skeleton,
} from '@/components/ui';

interface OrderSummary {
  id: string;
  number: string;
  status: string;
  total: number;
  paymentMethod: string;
  fulfillmentType: string;
  minStartDate: string;
  maxEndDate: string;
  createdAt: string;
  itemsCount: number;
  preview: Array<{ name: LocalizedText; image: string | null; size: string | null }>;
}

interface OrderDetail extends OrderSummary {
  itemsTotal: number;
  accessoriesTotal: number;
  deliveryCost: number;
  promoDiscount: number;
  depositTotal: number;
  depositMode: string;
  lateFeeTotal: number;
  fineTotal: number;
  overdueDays: number;
  addressSnapshot: { addressLine: string; comment: string | null } | null;
  pickupPoint: { name: LocalizedText; address: LocalizedText; phone: string | null } | null;
  comment: string | null;
  items: Array<{
    id: string;
    snapshot: {
      name: LocalizedText;
      image: string | null;
      size: string | null;
      color: { hex: string } | null;
    };
    qty: number;
    startDate: string;
    endDate: string;
    days: number;
    subtotal: number;
    accessories: Array<{ id: string; snapshot: { name: LocalizedText }; subtotal: number }>;
  }>;
  history: Array<{ id: string; toStatus: string; comment: string | null; createdAt: string }>;
}

/** До какого момента клиент отменяет заказ сам — правило одно с бэкендом. */
const CANCELLABLE_BY_CLIENT: readonly string[] = SELF_CANCELLABLE_STATUSES;

/** Статусы, на которых заказ ещё в работе, — их подсвечиваем. */
const ACTIVE_STATUSES = new Set([
  'NEW',
  'AWAITING_PAYMENT',
  'PAID',
  'AWAITING_ADMIN_CONFIRMATION',
  'AWAITING_MYID',
  'VERIFIED',
  'PREPARING',
  'READY_FOR_PICKUP',
  'HANDED_TO_DELIVERY',
  'DELIVERED',
  'RENTAL_ACTIVE',
  'AWAITING_RETURN',
]);

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone = status === 'CANCELLED' ? 'red' : ACTIVE_STATUSES.has(status) ? 'green' : 'neutral';

  return <Badge tone={tone}>{t(`orders.status.${status}`)}</Badge>;
}

export function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authorized } = useSession();

  const orders = useQuery({
    queryKey: ['orders'],
    queryFn: async () => (await api.get<OrderSummary[]>('/orders')).data,
    enabled: authorized,
  });

  if (!authorized) {
    return (
      <Screen>
        <SectionTitle>{t('orders.title')}</SectionTitle>
        <EmptyState title={t('common.outsideTelegram')} />
      </Screen>
    );
  }

  return (
    <Screen>
      <SectionTitle>{t('orders.title')}</SectionTitle>

      {orders.isLoading ? (
        <>
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </>
      ) : !orders.data || orders.data.length === 0 ? (
        <EmptyState
          title={t('orders.empty')}
          action={
            <Button variant="secondary" onClick={() => navigate('/categories')}>
              {t('cart.goToCatalog')}
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {orders.data.map((order) => (
            <Link
              key={order.id}
              to={`/orders/${order.id}`}
              className="block rounded-card bg-surface p-3 shadow-soft active:opacity-70"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">
                  {t('orders.number')} {order.number}
                </span>
                <StatusBadge status={order.status} />
              </div>

              <p className="mt-1 text-xs text-muted">
                {dateRange(order.minStartDate, order.maxEndDate)}
              </p>

              <div className="mt-2 flex items-center gap-2">
                {order.preview.slice(0, 3).map((item, index) =>
                  item.image ? (
                    <img
                      key={index}
                      src={item.image}
                      alt=""
                      className="size-10 rounded-lg object-cover"
                    />
                  ) : (
                    <span
                      key={index}
                      className="grid size-10 place-items-center rounded-lg bg-line text-muted/40"
                    >
                      <Icon name="shirt" size={18} stroke={1.4} />
                    </span>
                  ),
                )}
                <span className="ml-auto text-sm font-semibold">{money(order.total)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Screen>
  );
}

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => setBackButton(() => navigate('/orders')), [navigate]);

  const order = useQuery({
    queryKey: ['order', id],
    queryFn: async () => (await api.get<OrderDetail>(`/orders/${id}`)).data,
    // Пока заказ в работе, статус может измениться на стороне склада
    refetchInterval: 30_000,
  });

  if (order.isLoading || !order.data) {
    return (
      <Screen title={t('orders.number')}>
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-40 w-full rounded-card" />
      </Screen>
    );
  }

  const o = order.data;

  return (
    <Screen title={`${t('orders.number')} №${o.number}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted">
          {t('orders.created')} {new Date(o.createdAt).toLocaleDateString('ru-RU')}
        </p>
        <StatusBadge status={o.status} />
      </div>

      <section className="space-y-2">
        <p className="text-sm font-medium">{t('orders.items')}</p>
        {o.items.map((item) => (
          <div key={item.id} className="flex gap-3 rounded-card bg-surface p-3 shadow-soft">
            {item.snapshot.image ? (
              <img src={item.snapshot.image} alt="" className="size-16 rounded-xl object-cover" />
            ) : (
              <span className="grid size-16 place-items-center rounded-xl bg-line text-muted/40">
                <Icon name="shirt" size={26} stroke={1.3} />
              </span>
            )}

            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{localized(item.snapshot.name)}</p>
              <p className="flex items-center gap-1.5 text-xs text-muted">
                {item.snapshot.size}
                {item.snapshot.color && (
                  <span
                    className="inline-block size-2.5 rounded-full border border-line"
                    style={{ background: item.snapshot.color.hex }}
                  />
                )}
              </p>
              <p className="text-xs text-muted">
                {dateRange(item.startDate, item.endDate)} ·{' '}
                {t('product.days', { count: item.days })}
              </p>
              {item.accessories.length > 0 && (
                <p className="text-xs text-muted">
                  + {item.accessories.map((a) => localized(a.snapshot.name)).join(', ')}
                </p>
              )}
              <p className="mt-1 text-sm font-semibold">{money(item.subtotal)}</p>
            </div>
          </div>
        ))}
      </section>

      <Card className="space-y-1">
        <Row label={t('cart.rent')} value={money(o.itemsTotal)} />
        {o.accessoriesTotal > 0 && (
          <Row label={t('cart.accessories')} value={money(o.accessoriesTotal)} />
        )}
        {o.promoDiscount > 0 && (
          <Row label={t('checkout.promo')} value={`−${money(o.promoDiscount)}`} />
        )}
        {o.fulfillmentType === 'DELIVERY' && (
          <Row
            label={t('checkout.delivery_cost')}
            value={o.deliveryCost === 0 ? t('checkout.deliveryFree') : money(o.deliveryCost)}
          />
        )}
        {o.depositTotal > 0 && (
          <Row
            label={t('cart.deposit')}
            hint={
              o.depositMode === 'CASH_ON_PICKUP' ? t('checkout.cashHint') : t('cart.depositHint')
            }
            value={money(o.depositTotal)}
          />
        )}
        {o.overdueDays > 0 && (
          <Row
            label={t('orders.lateFee', { count: o.overdueDays })}
            value={money(o.lateFeeTotal)}
          />
        )}
        {o.fineTotal > 0 && (
          <Row label={t('orders.fine')} hint={t('orders.fineHint')} value={money(o.fineTotal)} />
        )}
        <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2">
          <span className="font-medium">{t('orders.total')}</span>
          <span className="text-lg font-semibold">{money(o.total)}</span>
        </div>
      </Card>

      <Card className="space-y-1 text-sm">
        <Row
          label={t('orders.delivery')}
          value={o.fulfillmentType === 'DELIVERY' ? t('checkout.delivery') : t('checkout.pickup')}
        />
        {o.addressSnapshot && <p className="text-xs text-muted">{o.addressSnapshot.addressLine}</p>}
        {o.pickupPoint && (
          <p className="text-xs text-muted">
            {localized(o.pickupPoint.name)} · {localized(o.pickupPoint.address)}
          </p>
        )}
        <Row label={t('orders.payment')} value={t(`payment.method.${o.paymentMethod}`)} />
        {o.comment && <p className="text-xs text-muted">{o.comment}</p>}
      </Card>

      <PaymentsBlock orderId={o.id} />

      <section className="space-y-2">
        <p className="text-sm font-medium">{t('orders.history')}</p>
        <div className="space-y-1.5">
          {o.history.map((entry) => (
            <div key={entry.id} className="flex items-baseline gap-2 text-xs">
              <span className="text-muted">
                {new Date(entry.createdAt).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span>{t(`orders.status.${entry.toStatus}`)}</span>
            </div>
          ))}
        </div>
      </section>

      <MyIdBlock order={o} />

      <OrderActions order={o} />
    </Screen>
  );
}

interface MyIdState {
  status: string | null;
  url?: string;
  failureReason?: string | null;
  attemptsLeft?: number;
  canRetry: boolean;
}

/**
 * Верификация личности перед выдачей.
 *
 * Страница провайдера открывается внешней ссылкой, результат приходит
 * вебхуком — поэтому пока ждём, опрашиваем статус. Наличные идут мимо MyID
 * (Q9): там гарантией служит паспорт, оставленный при получении.
 */
function MyIdBlock({ order }: { order: { id: string; status: string; paymentMethod: string } }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const needed = order.status === 'AWAITING_MYID' && order.paymentMethod !== 'CASH';

  const state = useQuery({
    queryKey: ['myid', order.id],
    queryFn: async () => (await api.get<MyIdState>(`/myid/orders/${order.id}`)).data,
    enabled: needed,
    // Результат приходит вебхуком, поэтому ждём его опросом
    refetchInterval: needed ? 5_000 : false,
  });

  const start = useMutation({
    mutationFn: async () =>
      (await api.post<{ url?: string }>(`/myid/orders/${order.id}/start`, {})).data,
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['myid', order.id] });
      if (data.url) {
        haptic.tap();
        openLink(data.url);
      }
    },
    onError: (e) => setError(errorMessage(e)),
  });

  if (!needed) return null;

  const status = state.data?.status;
  const waiting = status === 'IN_PROGRESS' || status === 'PENDING';

  return (
    <Card className="space-y-3">
      <div>
        <p className="font-medium">{t('myid.title')}</p>
        <p className="mt-1 text-sm text-muted">{t('myid.hint')}</p>
      </div>

      {status === 'FAILED' && (
        <p className="text-sm text-error">{state.data?.failureReason ?? t('myid.failed')}</p>
      )}
      {status === 'EXPIRED' && <p className="text-sm text-muted">{t('myid.expired')}</p>}

      {waiting && state.data?.url ? (
        <>
          <Button className="w-full" onClick={() => openLink(state.data!.url!)}>
            {t('myid.continue')}
          </Button>
          <p className="text-center text-xs text-muted">{t('myid.waiting')}</p>
        </>
      ) : (
        <Button
          className="w-full"
          disabled={start.isPending || state.data?.canRetry === false}
          onClick={() => start.mutate()}
        >
          {start.isPending ? t('common.loading') : t('myid.start')}
        </Button>
      )}

      {typeof state.data?.attemptsLeft === 'number' && state.data.attemptsLeft <= 2 && (
        <p className="text-center text-xs text-muted">
          {t('myid.attemptsLeft', { count: state.data.attemptsLeft })}
        </p>
      )}

      {error && <p className="text-center text-sm text-error">{error}</p>}
    </Card>
  );
}

interface PaymentRow {
  id: string;
  provider: string;
  purpose: string;
  amount: number;
  status: string;
  paidAt: string | null;
}

/** История платежей заказа: клиент должен видеть, что деньги дошли. */
function PaymentsBlock({ orderId }: { orderId: string }) {
  const { t } = useTranslation();

  const payments = useQuery({
    queryKey: ['payments', orderId],
    queryFn: async () => (await api.get<PaymentRow[]>(`/payments/orders/${orderId}`)).data,
  });

  if (!payments.data || payments.data.length === 0) return null;

  return (
    <section className="space-y-2">
      <p className="text-sm font-medium">{t('payment.history')}</p>
      {payments.data.map((payment) => (
        <Card key={payment.id} className="flex items-center justify-between gap-3 text-sm">
          <div className="min-w-0">
            <p>{t(`payment.method.${payment.provider}`)}</p>
            {payment.paidAt && (
              <p className="text-xs text-muted">
                {new Date(payment.paidAt).toLocaleString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="font-semibold">{money(payment.amount)}</span>
            <Badge
              tone={
                payment.status === 'PAID' ? 'green' : payment.status === 'PENDING' ? 'gold' : 'red'
              }
            >
              {t(`payment.status.${payment.status}`)}
            </Badge>
          </div>
        </Card>
      ))}
    </section>
  );
}

/**
 * Оплата и отмена. Ссылку на оплату открываем через `openLink`: платёжка
 * не работает внутри WebView Telegram, а результат всё равно приходит
 * вебхуком, а не возвратом в приложение.
 */
function OrderActions({
  order,
}: {
  order: { id: string; status: string; paymentMethod: string; total: number };
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const pay = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ url: string | null; reason?: string }>(
          `/payments/orders/${order.id}/checkout`,
          {},
        )
      ).data,
    onSuccess: (data) => {
      if (!data.url) return setError(data.reason ?? t('common.error'));

      haptic.tap();
      openLink(data.url);
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const cancel = useMutation({
    mutationFn: async () => api.post(`/orders/${order.id}/cancel`, {}),
    onSuccess: () => {
      haptic.success();
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const payable = order.status === 'AWAITING_PAYMENT' && order.paymentMethod !== 'CASH';
  const cancellable = CANCELLABLE_BY_CLIENT.includes(order.status);

  if (!payable && !cancellable) return null;

  return (
    <div className="space-y-2">
      {payable && (
        <Button className="w-full" disabled={pay.isPending} onClick={() => pay.mutate()}>
          {pay.isPending ? t('common.loading') : `${t('orders.pay')} · ${money(order.total)}`}
        </Button>
      )}

      {payable && <p className="text-center text-xs text-muted">{t('payment.opensExternally')}</p>}

      {cancellable && (
        <Button
          variant="secondary"
          className="w-full"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {t('orders.cancel')}
        </Button>
      )}

      {error && <p className="text-center text-sm text-error">{error}</p>}
    </div>
  );
}
